import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GetMocktestQuestion } from "../../../Client/request";
import {
  getListeningAudioUrl,
  getListeningPreloadBatches,
  getListeningQuestionsFromStep,
  getQuestionId,
  isTerminalAudioStatus,
  LISTENING_PRELOAD_WINDOW,
  preloadAudioUrl,
} from "../utils/listeningPreload";

export const useListeningPreload = (
  questions = [],
  currentStep = 1,
  { preloadWindow = LISTENING_PRELOAD_WINDOW } = {}
) => {
  const [questionCache, setQuestionCache] = useState({});
  const [audioStatusByQuestionId, setAudioStatusByQuestionId] = useState({});
  const questionCacheRef = useRef({});
  const audioPromisesRef = useRef({});

  const listeningQuestions = useMemo(
    () => getListeningQuestionsFromStep(questions, currentStep),
    [questions, currentStep]
  );
  const { initialQuestions, backgroundQuestions } = useMemo(
    () => getListeningPreloadBatches(listeningQuestions, preloadWindow),
    [listeningQuestions, preloadWindow]
  );

  const setCachedQuestion = useCallback((questionId, questionData) => {
    questionCacheRef.current = {
      ...questionCacheRef.current,
      [questionId]: questionData,
    };
    setQuestionCache(questionCacheRef.current);
  }, []);

  const setAudioStatus = useCallback((questionId, status) => {
    setAudioStatusByQuestionId((previous) => {
      if (previous[questionId] === status) return previous;
      return {
        ...previous,
        [questionId]: status,
      };
    });
  }, []);

  const ensureQuestionData = useCallback(
    async (question, isCancelled) => {
      const questionId = getQuestionId(question);
      if (!questionId) return null;

      if (questionCacheRef.current[questionId]) {
        return questionCacheRef.current[questionId];
      }

      try {
        const response = await GetMocktestQuestion(question.QuestionId);
        if (isCancelled()) return null;

        if (response?.data) {
          setCachedQuestion(questionId, response.data);
          return response.data;
        }
      } catch (error) {
        console.error("Error preloading listening question:", error);
      }

      setAudioStatus(questionId, "error");
      return null;
    },
    [setAudioStatus, setCachedQuestion]
  );

  const ensureAudioPreloaded = useCallback(
    async (question, questionData, isCancelled) => {
      const questionId = getQuestionId(question);
      if (!questionId) return;

      const audioUrl = getListeningAudioUrl(questionData);
      if (!audioUrl) {
        setAudioStatus(questionId, "no-audio");
        return;
      }

      if (!audioPromisesRef.current[questionId]) {
        setAudioStatus(questionId, "loading");
        audioPromisesRef.current[questionId] = preloadAudioUrl(audioUrl);
      }

      const status = await audioPromisesRef.current[questionId];
      if (!isCancelled()) {
        setAudioStatus(questionId, status);
      }
    },
    [setAudioStatus]
  );

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    const preload = async () => {
      const initialQuestionDataPairs = await Promise.all(
        initialQuestions.map(async (question) => ({
          question,
          questionData: await ensureQuestionData(question, isCancelled),
        }))
      );

      if (isCancelled()) return;

      await Promise.all(
        initialQuestionDataPairs.map(({ question, questionData }) =>
          questionData
            ? ensureAudioPreloaded(question, questionData, isCancelled)
            : Promise.resolve()
        )
      );

      backgroundQuestions.forEach(async (question) => {
        if (isCancelled()) return;
        const questionData = await ensureQuestionData(question, isCancelled);
        if (!questionData || isCancelled()) return;
        ensureAudioPreloaded(question, questionData, isCancelled);
      });
    };

    if (listeningQuestions.length > 0) {
      preload();
    }

    return () => {
      cancelled = true;
    };
  }, [backgroundQuestions, ensureAudioPreloaded, ensureQuestionData, initialQuestions, listeningQuestions.length]);

  const initialPreloadReady = initialQuestions.every((question) =>
    isTerminalAudioStatus(audioStatusByQuestionId[getQuestionId(question)])
  );

  const getCachedQuestionData = useCallback(
    (question) => questionCache[getQuestionId(question)] || null,
    [questionCache]
  );

  const getAudioStatus = useCallback(
    (question) => audioStatusByQuestionId[getQuestionId(question)] || "idle",
    [audioStatusByQuestionId]
  );

  return {
    getAudioStatus,
    getCachedQuestionData,
    initialPreloadReady,
    initialPreloadTotal: initialQuestions.length,
    listeningPreloadTotal: listeningQuestions.length,
    questionCache,
  };
};
